import { AppThunk, IState } from '../../../../../store/types';
import { VocabularyEffects } from '../../../../../effects/phrase/vocabulary-effects';
import {
  getActiveGroupTargetLanguage,
  getGroupNativeLanguage,
  getGroupTargetLanguage,
  getNativeLanguage,
  getUserGroupById
} from '../../../../../store/models/selectors';
import { EVocabularyPhraseType, IVocabularyPhrase } from '../../../../../types/common';
import { PhraseDetailsEffects } from '../../../../../effects/phrase-details/phrase-details-effects';
import { PlayerController } from '../../../../../effects/player/manager/playerController';
import {
  IPhraseSelectResult,
  PhraseSelectPreparator
} from '../../../../../effects/phrase/utils/phrase-select-preparator';
import { PhraseListSelectors } from '../../../../../store/phrase-list/selectors';
import { LangUtil } from '../../../../../../common/utils/lang-util';
import { PhraseContextEditorActions } from '../../../../../store/phrase-context-editor/actions';
import { EShortcutsListenerContainer, setShortcutsListenerContainer } from '../../../../../effects/shortcutsListener';
import { SelectionUtil } from '../../../../../../common/utils/selection-util';
import { PhraseListActions } from '../../../../../store/phrase-list/actions';
import { getMainLayoutPhrasesColumnTab } from '../../../../../store/general/selectors';
import { CaptionsSelectionPopupActions } from '../../../../../store/captions-selection-popup/actions';
import { textSpeaker } from '../../../../../effects/textSpeaker';
import { getVideoPhraseNoteByPhraseId } from '../../../../../store/videos/selectors';
import { getState } from '../../../../../store';
import { HtmlUtils } from '../../../../../../common/utils/html-utils';
import { PhraseEffects } from '../../../../../effects/phrase/PhraseEffects';
import { PhraseNoteEffects } from '../../../../../effects/phraseNoteEffects';
import { historyPush } from '../../../../../../common/utils/historyHelper';
import { MainLayoutEffects } from '../../../../../effects/mainLayoutEffects';
import { CaptionsSelectionPopupSelectors } from '../../../../../store/captions-selection-popup/selectors';
import { EPhrasePopupType } from '../../../../../store/phrase-list/types';

export class VocabularyPhraseEvents {

  public static onPlayPhrase(phraseId: number, isPause?: boolean): AppThunk {
    return async (
      dispatch,
      getState
    ) => {
      if (isPause) {
        PlayerController.getInstance().pause();
      } else {
        const phrase = VocabularyPhraseEvents.getPhrase(getState(), phraseId);
        if (phrase) {
          PlayerController.getInstance().playPhrase(phrase);
          setShortcutsListenerContainer(EShortcutsListenerContainer.PHRASES);
        }
        dispatch(CaptionsSelectionPopupActions.setCurrentSelection(null as any));
      }
    }
  }

  public static onSpeechText(
    text: string,
    withNote: boolean
  ): AppThunk {
    return (
      dispatch,
      getState
    ) => {
      if (textSpeaker.isPlay()) {
        textSpeaker.stop();
      } else {
        PlayerController.getInstance().pause();
        const lang = withNote ? undefined : getActiveGroupTargetLanguage(getState())?.code;
        textSpeaker.speak(text, lang);
      }
    }
  }

  public static onClickPhrase(phraseId: number): AppThunk {
    return async (
      dispatch,
      getState
    ) => {
      return dispatch(this.showPhrase(phraseId));
    }
  }

  public static onRemovePhrase(phraseId: number): AppThunk {
    return async (
      dispatch,
      getState
    ) => {
      dispatch(VocabularyEffects.removePhrasesRelated(phraseId));
    }
  }

  public static onEditPhrase(phraseId: number): AppThunk {
    return async (
      dispatch,
      getState
    ) => {
      PlayerController.getInstance().resetMode();
      dispatch(PhraseContextEditorActions.activate(phraseId));
    }
  }

  public static onContextDelete(
    phraseId: number,
  ): AppThunk {
    return async (
      dispatch,
      getState
    ) => {
      PhraseEffects.deleteContext(phraseId);
    }
  }

  public static onSelectText(phraseId: number, selection: Selection): AppThunk {
    return (
      dispatch,
      getState
    ) => {
      dispatch(this.showPhrase(phraseId, selection));
    }
  }

  private static getPhrase(state: IState, phraseId: number) {
    return PhraseListSelectors.findPhraseById(state, phraseId);
  }

  private static getPhraseSelectionText(selection: Selection, phrase: IVocabularyPhrase, langCode: string): string {
    let s = selection.toString().trim().replace(/[\n\r]/gm, ' ');
    if (!s) return '';

    let fullText = phrase.fullPhrase.replace(/[\n\r]/gm, ' ');

    const note = getVideoPhraseNoteByPhraseId(getState(), phrase.wordPhraseId);
    if (note) {
      const noteText = HtmlUtils.removeHtmlTags(note.text, ' ');
      fullText += ' ' + noteText;
    }

    const startPosition = fullText.indexOf(s);
    if (startPosition >= 0) {
      const result: IPhraseSelectResult = {
        element: undefined, endCaptionIndex: 0, endTime: 0, startCaptionIndex: 0, startTime: 0,
        text: s,
        startPosition,
        endPosition: startPosition + s.length
      }
      const useCheckSpanWords  = !LangUtil.isHieroglyphLang(langCode);
      if (useCheckSpanWords) {
        const selection = SelectionUtil.getSelectedText();
        if (selection) {
          const ret = new PhraseSelectPreparator(selection, null, [{
            startTime:0, endTime:0, duration:0, text: fullText
          }], [], [], null, true).checkSpanWords(result);
          s = ret.text;
        }
      }
    }

    return s;
  }

  public static onOpenNote (
    phraseId: number
  ): AppThunk {
    return async (
      dispatch,
      getState
    ) => {
      return dispatch(this.showPhrase(phraseId, undefined, true));
    }
  }

  public static onSavePhrase (
    phraseId: number
  ): AppThunk {
    return async (
      dispatch,
      getState
    ) => {
      const contextPhrase = VocabularyPhraseEvents.getPhrase(getState(), phraseId);
      const wordPhrase = contextPhrase?.wordPhraseId &&
        VocabularyPhraseEvents.getPhrase(getState(), contextPhrase?.wordPhraseId);
      if (wordPhrase && contextPhrase) {
        await PhraseEffects.saveTranslatedPhrases(wordPhrase, contextPhrase);
      }
    }
  }

  private static speechText(text: string, priorityLang: string,) {
    const state = getState();
    if (!CaptionsSelectionPopupSelectors.getAutoSpeechText(state)) return;

    textSpeaker.speak(text, priorityLang, false, undefined, priorityLang);
  }

  private static showPhrase(
    phraseId: number,
    selection?: Selection,
    note?: boolean,
  ): AppThunk {
    return async (
      dispatch,
      getState
    ) => {
      const state = getState();
      const phrase = VocabularyPhraseEvents.getPhrase(state, phraseId);
      if (!phrase?.userGroupId) return;

      const group = getUserGroupById(state, phrase.userGroupId);
      if (!group) return;

      const targetLang = getGroupTargetLanguage(state, group);
      if (!targetLang) return;

      const nativeLang = getGroupNativeLanguage(state, group) || getNativeLanguage(state);

      PlayerController.getInstance().resetMode();

      const text = selection
        ? VocabularyPhraseEvents.getPhraseSelectionText(selection, phrase, targetLang.code)
        : phrase.highlighted;

      // const openDefaultTab = !note || phrase.type !== EVocabularyPhraseType.LESSON;

      PhraseDetailsEffects.showFromSavedPhrase(targetLang, nativeLang, nativeLang, text, phrase, EPhrasePopupType.SAVED);

      if (!selection) {
        dispatch(CaptionsSelectionPopupActions.setCurrentSelection({wordPhraseId: phrase.wordPhraseId, contextPhraseId: phrase.id}));
      }
      dispatch(PhraseListActions.setForwardPhraseId(phraseId));
      dispatch(PhraseListActions.setPlayCaptionByPhraseId(phraseId));
      
      PhraseEffects.seekPhrase(phrase);
      if (note) {
        dispatch(PhraseListActions.setNotePopup(true));
      } else {
        PhraseNoteEffects.checkCloseNotePopup(phrase);
        this.speechText(text, targetLang.code);
      }

      historyPush({
        state: { page: 'translate' },
        title: 'Translate',
        onpopstate: () => {
          dispatch(PhraseListActions.setPhrasePopup(null as any));
          dispatch(PhraseListActions.setNotePopup(false));
        }
      });
    }
  }

}